import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { Calendar, Clock, ArrowRight, CheckCircle2, Beaker, Thermometer, Gauge, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Guide() {
  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 88; // header height
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  const tocItems = [
    { id: 'intro', label: '什么是特种橡胶板材' },
    { id: 'material', label: '核心材料对比' },
    { id: 'selection', label: '如何选型' },
    { id: 'fkm', label: 'FKM氟橡胶板深度解析' },
    { id: 'epdm', label: 'EPDM三元乙丙橡胶板' },
    { id: 'nbr', label: 'NBR丁腈橡胶板' },
    { id: 'custom', label: '定制配方服务' },
    { id: 'contact', label: '立即获取专业选型建议' },
  ];

  const materials = [
    {
      name: 'FKM氟橡胶',
      bestUse: '强腐蚀、高温密封',
      tempRange: '-30°C ~ +220°C',
      industries: '化工石化、航空航天',
    },
    {
      name: 'EPDM三元乙丙',
      bestUse: '耐候、绝缘',
      tempRange: '-40°C ~ +150°C',
      industries: '建筑幕墙、电力能源',
    },
    {
      name: 'NBR丁腈橡胶',
      bestUse: '耐油、机械密封',
      tempRange: '-30°C ~ +100°C',
      industries: '液压系统、汽车工业',
    },
    {
      name: 'NR天然橡胶',
      bestUse: '高弹、耐磨',
      tempRange: '-50°C ~ +80°C',
      industries: '矿山采掘、物料输送',
    },
    {
      name: 'CR氯丁橡胶',
      bestUse: '阻燃、耐候',
      tempRange: '-35°C ~ +100°C',
      industries: '船舶建筑、海洋工程',
    },
  ];

  const selectionSteps = [
    {
      step: '01',
      title: '确定工作介质',
      description: '明确橡胶板将接触的化学物质、油类、溶剂或气体类型，这是选材的首要依据。',
      icon: Beaker,
    },
    {
      step: '02',
      title: '选择材料类型',
      description: '根据介质特性匹配对应材料：耐油选NBR，耐酸碱选FKM，耐候选EPDM。',
      icon: CheckCircle2,
    },
    {
      step: '03',
      title: '确认温度范围',
      description: '核实工作环境的最低和最高温度，确保材料温度范围完全覆盖实际工况。',
      icon: Thermometer,
    },
    {
      step: '04',
      title: '验证机械性能',
      description: '检查拉伸强度、硬度、伸长率等参数是否满足密封压力和形变要求。',
      icon: Gauge,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-0.5 bg-primary" />
            <span className="font-mono text-xs font-bold tracking-widest uppercase text-primary">
              TECHNICAL GUIDE
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            工业橡胶板材选型完全指南
          </h1>
          <p className="text-xl text-muted-foreground mb-6">
            COMPLETE GUIDE TO INDUSTRIAL RUBBER SHEET SELECTION
          </p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>发布日期：2026-01-15</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>预计阅读：8 分钟</span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">
          <motion.article
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <section id="intro" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                什么是特种橡胶板材
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                特种橡胶板材是工业密封系统中的核心材料，广泛应用于化工、石化、建筑、矿山、电力等领域。与普通橡胶相比，特种橡胶通过精确的配方设计和工艺控制，能够在极端温度、强腐蚀介质、高压力等苛刻工况下保持稳定的密封性能和机械强度。
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                选择合适的橡胶板材，不仅关系到设备的密封效果和使用寿命，更直接影响生产安全和运营成本。本指南将系统介绍五大主流特种橡胶材料的特性、应用场景和选型方法。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="font-mono text-xs font-bold text-primary mb-2">FKM</div>
                  <p className="text-sm text-muted-foreground">耐强腐蚀、超高温稳定，化工石化首选</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="font-mono text-xs font-bold text-primary mb-2">EPDM</div>
                  <p className="text-sm text-muted-foreground">卓越耐候性、优异绝缘，户外建筑专用</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="font-mono text-xs font-bold text-primary mb-2">NBR</div>
                  <p className="text-sm text-muted-foreground">优异耐油性、高机械强度，液压系统标配</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="font-mono text-xs font-bold text-primary mb-2">NR</div>
                  <p className="text-sm text-muted-foreground">极高弹性、超强耐磨，矿山输送首选</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="font-mono text-xs font-bold text-primary mb-2">CR</div>
                  <p className="text-sm text-muted-foreground">固有阻燃、耐海水腐蚀，船舶建筑安全材料</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="font-mono text-xs font-bold text-primary mb-2">ESD</div>
                  <p className="text-sm text-muted-foreground">防静电、导电可控，电子半导体防爆专用</p>
                </div>
              </div>
            </section>

            <section id="material" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                核心材料对比
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                以下表格对比了五种主流特种橡胶材料的核心特性，帮助您快速定位适合的材料类型：
              </p>
              <div className="overflow-x-auto">
                <table className="table-industrial w-full">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left">材料</th>
                      <th className="px-4 py-3 text-left">最佳用途</th>
                      <th className="px-4 py-3 text-left">温度范围</th>
                      <th className="px-4 py-3 text-left">典型行业</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materials.map((material, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 font-semibold">{material.name}</td>
                        <td className="px-4 py-3">{material.bestUse}</td>
                        <td className="px-4 py-3">
                          <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                            {material.tempRange}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-sm">{material.industries}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="selection" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                如何选型
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                正确的选型流程能够确保橡胶板材在实际应用中发挥最佳性能。遵循以下四步法，系统评估您的应用需求：
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectionSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="card-industrial p-6"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="font-mono text-xs font-bold text-primary mb-2">
                            STEP {step.step}
                          </div>
                          <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>

            <section id="fkm" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                FKM氟橡胶板深度解析
              </h2>
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <div className="inline-block px-3 py-1 bg-primary/10 border border-primary/30 rounded text-sm font-mono font-bold text-primary mb-4">
                  FKM / VITON®
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  FKM氟橡胶是耐化学腐蚀性能最优异的橡胶材料之一，能够在-30°C至+220°C的宽温度范围内保持稳定的密封性能。其分子结构中的氟原子赋予了材料卓越的耐强酸、强碱、有机溶剂和石油基油品的能力。
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-primary" />
                    温度性能
                  </h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>标准型：-30°C ~ +220°C</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>高温型：-20°C ~ +250°C</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>低温型：-40°C ~ +200°C</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-primary" />
                    机械性能
                  </h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>拉伸强度：≥10.0 MPa</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>断裂伸长率：≥150%</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>硬度范围：60-90 Shore A</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-3">典型应用场景</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    化工石化管道密封系统
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    航空航天燃油系统密封件
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    汽车发动机高温密封垫片
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    强腐蚀性介质环境防护
                  </div>
                </div>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-bold text-primary mb-2">核心优势</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• 优异的耐化学腐蚀性，可耐受浓硫酸、浓硝酸等强腐蚀介质</li>
                  <li>• 宽温度范围适应性，高温下仍保持良好的机械性能</li>
                  <li>• 长期耐老化性能，使用寿命可达10年以上</li>
                  <li>• 低压缩永久变形，密封可靠性高</li>
                </ul>
              </div>
            </section>

            <section id="epdm" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                EPDM三元乙丙橡胶板
              </h2>
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <div className="inline-block px-3 py-1 bg-primary/10 border border-primary/30 rounded text-sm font-mono font-bold text-primary mb-4">
                  EPDM
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  EPDM三元乙丙橡胶以其卓越的耐候性和优异的电绝缘性能著称，是户外建筑和电力系统的首选密封材料。其饱和的分子结构使其具有极强的抗臭氧、抗紫外线老化能力，即使在恶劣的户外环境中也能保持长期稳定的性能。
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-primary" />
                    温度性能
                  </h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>标准型：-40°C ~ +150°C</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>绝缘型：-40°C ~ +120°C</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>低温性能优异，适合寒冷地区</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-primary" />
                    机械性能
                  </h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>拉伸强度：≥8.0 MPa</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>断裂伸长率：≥300%</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>硬度范围：50-80 Shore A</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-3">典型应用场景</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    建筑幕墙密封系统
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    屋面防水卷材
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    电力绝缘垫
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    户外设备防护罩
                  </div>
                </div>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-bold text-primary mb-2">核心优势</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• 卓越的耐候性，可在户外环境中使用20年以上</li>
                  <li>• 优异的电绝缘性，击穿电压可达30kV/mm</li>
                  <li>• 抗臭氧老化能力强，不易龟裂</li>
                  <li>• 环保无毒，符合RoHS和REACH标准</li>
                </ul>
              </div>
            </section>

            <section id="nbr" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                NBR丁腈橡胶板
              </h2>
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <div className="inline-block px-3 py-1 bg-primary/10 border border-primary/30 rounded text-sm font-mono font-bold text-primary mb-4">
                  NBR / BUNA-N
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  NBR丁腈橡胶是耐油性能最优异的通用橡胶材料，广泛应用于液压系统、机械设备和汽车工业。其丙烯腈含量越高，耐油性越好，但低温性能会相应降低。通过调整丙烯腈含量，可以在耐油性和低温性能之间取得最佳平衡。
              </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-primary" />
                    温度性能
                  </h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>标准型：-30°C ~ +100°C</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>高强型：-25°C ~ +90°C</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>适合常温工况，性价比高</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-primary" />
                    机械性能
                  </h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>拉伸强度：≥12.0 MPa</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>断裂伸长率：≥350%</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span>硬度范围：60-80 Shore A</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-3">典型应用场景</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    液压系统密封件
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    机械设备垫片
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    油田开采设备
                  </div>
                  <div className="bg-muted/50 border border-border rounded p-3 text-sm text-muted-foreground">
                    汽车传动系统
                  </div>
                </div>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-bold text-primary mb-2">核心优势</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• 优异的耐油性，可耐受矿物油、液压油、润滑油等</li>
                  <li>• 高机械强度，拉伸强度可达15 MPa以上</li>
                  <li>• 良好的耐磨性，适合动态密封应用</li>
                  <li>• 性价比高，是工业密封的经济型选择</li>
                </ul>
              </div>
            </section>

            <section id="custom" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 border-l-4 border-primary pl-4">
                定制配方服务
              </h2>
              <div className="bg-gradient-to-br from-card to-muted/30 border border-border rounded-lg p-8 mb-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                    <Wrench className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">为什么需要定制配方？</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      标准橡胶板材虽然能够满足大多数应用需求，但在某些特殊工况下（如极端温度、复合介质、特殊硬度要求等），定制配方能够提供更精确的性能匹配，确保密封系统的可靠性和经济性。
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-bold mb-3">我们提供的定制服务</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <span>配方优化：根据工况调整材料配比</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <span>性能定制：调整硬度、拉伸、伸长率等参数</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <span>颜色定制：提供多种颜色选择</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <span>尺寸定制：厚度、宽度、长度按需生产</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <span>异形加工：冲压、模切、粘接等深加工</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-3">交付周期</h4>
                    <div className="space-y-3">
                      <div className="bg-background/50 border border-border rounded p-3">
                        <div className="font-mono text-xs font-bold text-primary mb-1">标准品</div>
                        <div className="text-sm text-muted-foreground">3-7个工作日出货</div>
                      </div>
                      <div className="bg-background/50 border border-border rounded p-3">
                        <div className="font-mono text-xs font-bold text-primary mb-1">定制配方</div>
                        <div className="text-sm text-muted-foreground">10-20个工作日（含打样）</div>
                      </div>
                      <div className="bg-background/50 border border-border rounded p-3">
                        <div className="font-mono text-xs font-bold text-primary mb-1">异形加工</div>
                        <div className="text-sm text-muted-foreground">15-30个工作日（含开模）</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-primary">技术支持：</strong>
                  我们的技术团队拥有20年以上的橡胶配方研发经验，可为您提供从材料选型、配方设计到应用测试的全流程技术支持。所有定制产品均经过严格的质量检测，确保符合ISO 9001质量标准和客户的技术要求。
                </p>
              </div>
            </section>

            <section id="contact" className="scroll-mt-24">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-lg p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">立即获取专业选型建议</h2>
                <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
                  无法确定哪种橡胶板材最适合您的应用？我们的技术团队随时为您提供免费的选型咨询和技术支持。
                </p>
                <Link
                  to={ROUTE_PATHS.CONTACT}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-bold rounded-lg hover:bg-primary/90 transition-all hover:scale-105"
                >
                  联系技术团队
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </section>
          </motion.article>

          <motion.aside
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="sticky top-24">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-mono text-xs font-bold tracking-widest uppercase text-primary mb-4">
                  目录 CONTENTS
                </h3>
                <nav className="space-y-2">
                  {tocItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className="block w-full text-left text-sm text-muted-foreground hover:text-primary transition-colors py-1 border-l-2 border-transparent hover:border-primary pl-3"
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </div>
  );
}